import { useContext, useReducer, useEffect, useMemo } from 'react';
import request from 'utils/request_ajax';
import { FilterContext } from 'components/shared/FilterContext/index';

/**
 * This component utilizes the applied filters under the hood, to make a request
 * to a remote endpoint using the provided `url`. It provides the request's
 * state (loading, error, and data) to its children for rendering purposes. The
 * request is made on mount and any time the filters are updated. You can
 * include additional params to the request using the `include` prop.
 *
 * @example
 *
 * // given I have the filter `{ groups: [1] }`, this component will send the
 * // following request on mount (and whenever the filters change):
 * //
 * // GET /test/path?groups[]=1&time_zone=America/New_York
 *
 * <FilteredRequest url="/test/path" include={{ timezone: 'America/New_York' }}>
 *   {({ data, loading, error }) => (
 *     <div>
 *       {loading ? 'Loading...' : JSON.stringify(data)}
 *     </div>
 *   )}
 * </FilteredRequest>
 */
const FilteredRequest = ({ children, include, url }) => {
  const filter = useContext(FilterContext);
  const [state, setState] = useReducer((prev, next) => ({ ...prev, ...next }), {
    loading: true,
    error: null,
    data: null,
  });

  const params = useMemo(
    () => ({ ...filter.params, ...include }),
    /**
     * @todo make sure `include` won't be causing infinite loops in the future
     * when used with more interactive components
     */
    [filter.params, include],
  );

  useEffect(() => {
    setState({ loading: true });
    request
      .get(url, params)
      .then(data => setState({ loading: false, error: null, data }))
      .fail(error => setState({ loading: false, error }));
  }, [params, url]);

  return children({ ...state, params });
};

export default FilteredRequest;
